import { useMutation } from '@apollo/client/react'
import { Box, Card, HStack, Image, Marquee, Text } from '@chakra-ui/react'
import React, { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaMapPin } from 'react-icons/fa'
import {
  LuHeartCrack,
  LuHeartHandshake,
  LuNetwork,
  LuPowerOff,
} from 'react-icons/lu'
import { toast } from 'react-toastify'

import { Heading } from '@/components/atoms/Heading'
import { InsuranceCertificateBadge } from '@/components/atoms/InsuranceCertificateBadge/InsuranceCertificateBadge'
import { ThreeDotsMenu } from '@/components/atoms/Menu/ThreeDotsMenu'
import { UserStackedAvatars } from '@/components/molecules/User/UserStackedAvatars'
import { Button } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog'
import {
  CommunityCard_CommunityFragment,
  GetUserCirclesDocument,
  GetUserCirclesQueryVariables,
  LeaveCircleMembershipDocument,
} from '@/graphql/api/generated'
import { CommunitiesRoutes, NetworksRoutes } from '@/routes/_hooks/routes.enum'
import { generatePath, Link } from '@/routing'

import { usePrefetchCommunity } from './usePrefetchCommunity'

export type NetworkSummary = {
  id: string
  slug: string
  name: string
  /**
   * The network's square mark, and named after the relation it must come from.
   *
   * It used to be `logo`, and callers hand raw query nodes straight to this type — so the field
   * name *is* the contract. `logo` on a Network now holds the horizontal lockup the white-label
   * feature composes at activation, and this is drawn in a 20px rounded square: cropping a 4:1
   * lockup to a square keeps its middle and loses the network entirely.
   */
  squareIcon?: { id?: string; contentUrl?: string | null } | null
}

const getCircleInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')

const NetworkLogoBox = ({
  network,
  logoBg,
}: {
  network: NetworkSummary
  logoBg: string
}) => (
  <Box
    w="20px"
    h="20px"
    borderRadius="6px"
    overflow="hidden"
    bg={logoBg}
    boxShadow="sm"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    {network.squareIcon?.contentUrl ? (
      <Image
        src={network.squareIcon.contentUrl}
        alt={network.name}
        w="full"
        h="full"
        objectFit="cover"
      />
    ) : (
      <Text
        fontFamily="heading"
        fontWeight={700}
        fontSize="8px"
        color="gray.700"
      >
        {getCircleInitials(network.name)}
      </Text>
    )}
  </Box>
)

type CommunityCardProps = {
  circle: CommunityCard_CommunityFragment
  borderRadius?: number
  circleMembership?: { id: string; status: string }
  listRefetchVariables?: GetUserCirclesQueryVariables
  isCompactTitle?: boolean
  isFullHeight?: boolean
  /**
   * When provided (even as []), renders the network strip at the bottom.
   * undefined = no strip (backward-compatible for card lists that don't need it).
   */
  networks?: NetworkSummary[]
  /**
   * Optional content pinned at the bottom of the card (e.g. a join button),
   * below the avatars/city row. Rendered above the full-card link overlay.
   */
  footer?: React.ReactNode
  /**
   * Drops the full-card link to the community page. Required inside a dialog: the route would
   * change behind the overlay, leaving the user on an unchanged screen with no way forward.
   */
  disableNavigation?: boolean
}

export const CommunityCard = memo(
  ({
    circle,
    borderRadius,
    circleMembership,
    listRefetchVariables,
    isCompactTitle = false,
    isFullHeight = false,
    networks,
    footer,
    disableNavigation = false,
  }: CommunityCardProps) => {
    const { t } = useTranslation()
    const prefetchHandlers = usePrefetchCommunity(circle.slug)
    const [leaveOpen, setLeaveOpen] = useState(false)
    const leaveDialogBg = useColorModeValue('blue.50', 'blue.900')

    const [leaveCircleMembership, { loading: leaveLoading }] = useMutation(
      LeaveCircleMembershipDocument,
      listRefetchVariables
        ? {
            refetchQueries: [
              {
                query: GetUserCirclesDocument,
                variables: listRefetchVariables,
              },
            ],
          }
        : undefined
    )

    const first3Users = useMemo(
      () =>
        circle.memberships?.edges
          ?.map((edge) => edge?.node?.user)
          .filter((user): user is NonNullable<typeof user> =>
            Boolean(user?.id)
          ),
      [circle.memberships?.edges]
    )

    const totalCount = circle.memberships?.totalCount

    const cardBg = useColorModeValue('white', 'gray.800')
    const logoBg = useColorModeValue('white', 'gray.700')
    const logoTextColor = useColorModeValue('gray.700', 'gray.100')
    const veilGradient = useColorModeValue(
      'linear-gradient(to bottom, rgba(255,255,255,0.5), rgba(255,255,255,0.88))',
      'linear-gradient(to bottom, rgba(20,30,50,0.55), rgba(20,30,50,0.90))'
    )
    const coverNameColor = useColorModeValue('gray.800', 'gray.50')
    const cityColor = useColorModeValue('gray.500', 'gray.400')

    // Network strip colors
    const networkStripBorder = useColorModeValue('gray.200', 'gray.900')
    const labelBg = useColorModeValue('gray.200', 'gray.950')
    const labelColor = useColorModeValue('black', 'white')
    const labelBorderRight = useColorModeValue('gray.300', 'gray.800')
    const marqueeBg = useColorModeValue('gray.50', 'gray.900')
    const networkNameColor = useColorModeValue('gray.800', 'gray.100')
    const networkLogoBg = useColorModeValue('white', 'gray.900')
    const noNetworkBg = useColorModeValue('gray.50', 'gray.750')
    const noNetworkBorder = useColorModeValue('gray.100', 'gray.700')
    const noNetworkColor = useColorModeValue('gray.400', 'gray.500')

    const coverImageUrl = useMemo(() => {
      const coverUrl = circle.cover?.contentUrl?.trim()
      return coverUrl || '/photos/community-blured.webp'
    }, [circle.cover?.contentUrl])

    const otherLabel = useMemo(() => {
      if (totalCount == null) return undefined
      const shownCount = first3Users?.length ?? 0
      const others = Math.max(0, totalCount - shownCount)
      if (others <= 0) return undefined
      return t('communities.card.members.avatarStacked.andXOthers', {
        count: others,
      })
    }, [totalCount, first3Users, t])

    const canLeave =
      !!circleMembership &&
      circleMembership.status === 'ok' &&
      Array.isArray(circle.activeUserActions) &&
      circle.activeUserActions.includes('leave') &&
      !!listRefetchVariables

    const networkCount = networks?.length ?? 0

    const cardInner = (
      <Card.Root
        position="relative"
        borderRadius={borderRadius ?? '22px'}
        p={0}
        overflow="hidden"
        bg={cardBg}
        h={isFullHeight ? 'full' : undefined}
        display={isFullHeight ? 'flex' : undefined}
        flexDirection={isFullHeight ? 'column' : undefined}
        boxShadow="0 8px 24px -16px hsl(220deg 25% 15% / 0.10)"
        transition="transform .25s, box-shadow .25s"
        _hover={{
          transform: 'translateY(-3px)',
          boxShadow: '0 18px 36px -16px hsl(220deg 25% 15% / 0.18)',
        }}
      >
        {/* Full-card link, INSIDE the card on purpose. The `_hover` transform above turns the
            card into a stacking context, so an overlay rendered as a sibling would paint above
            every child — including the footer — and swallow its clicks as soon as the pointer
            enters the card. Kept in the same stacking context, the footer's zIndex 2 wins. */}
        {!disableNavigation && (
          <Link
            to={generatePath(CommunitiesRoutes.Show, {
              slug: circle.slug,
            })}
            aria-label={circle.name}
            style={{ position: 'absolute', inset: 0, zIndex: 1 }}
            {...prefetchHandlers}
          />
        )}

        {/* Cover: blurred image + gradient veil + logo & name */}
        <Box position="relative" h="140px" overflow="hidden" flexShrink={0}>
          <Image
            src={coverImageUrl}
            alt=""
            w="full"
            h="full"
            objectFit="cover"
            filter="blur(2px) saturate(1.1)"
            transform="scale(1.1)"
          />
          <Box position="absolute" inset={0} background={veilGradient} />
          <Box
            position="absolute"
            inset={0}
            px={5}
            py={4}
            display="flex"
            alignItems="center"
            gap={4}
          >
            <Box
              h="80px"
              w="80px"
              borderRadius="18px"
              overflow="hidden"
              flexShrink={0}
              bg={logoBg}
              boxShadow="0 4px 12px -4px hsl(220deg 25% 15% / 0.18)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {circle.logo?.contentUrl ? (
                <Image
                  src={circle.logo.contentUrl}
                  alt={circle.name}
                  w="full"
                  h="full"
                  objectFit="cover"
                />
              ) : (
                <Text
                  fontFamily="heading"
                  fontWeight={800}
                  fontSize="20px"
                  color={logoTextColor}
                  userSelect="none"
                >
                  {getCircleInitials(circle.name)}
                </Text>
              )}
            </Box>
            <HStack gap={2} alignItems="start" w="full" minW={0}>
              <Heading
                title={circle.name}
                flex={1}
                minW={0}
                as="h3"
                fontWeight={600}
                fontSize={isCompactTitle ? { base: 'sm', md: 'md' } : '17px'}
                lineHeight="1.2"
                color={coverNameColor}
                css={{
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {circle.name}
              </Heading>
              <Box flexShrink={0}>
                <InsuranceCertificateBadge
                  insuranceCertificate={
                    (circle as any).insuranceCertificateDocument
                  }
                />
              </Box>
            </HStack>
          </Box>
        </Box>

        {/* Body: stacked avatars on the left, city on the right */}
        <HStack
          px={5}
          py={4}
          justify="space-between"
          align="center"
          flex={isFullHeight ? 1 : undefined}
        >
          <UserStackedAvatars
            users={first3Users}
            totalCount={totalCount}
            otherLabel={otherLabel}
          />
          {circle.city && (
            <HStack gap={1} flexShrink={0} color={cityColor}>
              <FaMapPin size={12} />
              <Text fontSize="12px">{circle.city}</Text>
            </HStack>
          )}
        </HStack>

        {/* Network strip — only rendered when the prop is provided */}
        {networks !== undefined &&
          (networkCount > 0 ? (
            <HStack
              gap={0}
              borderTop="1px solid"
              borderColor={networkStripBorder}
              overflow="hidden"
              flexShrink={0}
              align="stretch"
            >
              {/* Fixed label */}
              <HStack
                gap={2}
                px={3}
                py={3}
                bg={labelBg}
                flexShrink={0}
                color={labelColor}
                borderRight="2px solid"
                borderRightColor={labelBorderRight}
                align="center"
              >
                <LuHeartHandshake size={18} />
                <Text
                  fontSize="13px"
                  fontWeight={700}
                  fontFamily="heading"
                  lineHeight="1"
                  whiteSpace="nowrap"
                  textTransform="uppercase"
                  letterSpacing="0.03em"
                >
                  {t('communities.card.networks.member', {
                    count: networkCount,
                  })}
                </Text>
              </HStack>

              {/* Single network: static display; multiple: scrolling marquee */}
              {networkCount === 1 ? (
                <Link
                  to={generatePath(NetworksRoutes.MyShow, {
                    slug: networks[0].slug,
                  })}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <HStack
                    gap={2}
                    px={4}
                    py={3}
                    bg={marqueeBg}
                    h="full"
                    align="center"
                  >
                    <NetworkLogoBox
                      network={networks[0]}
                      logoBg={networkLogoBg}
                    />
                    <Text
                      fontSize="12px"
                      fontWeight={600}
                      color={networkNameColor}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {networks[0].name}
                    </Text>
                  </HStack>
                </Link>
              ) : (
                <Marquee.Root
                  flex={1}
                  minW={0}
                  speed={35}
                  pauseOnInteraction
                  autoFill
                  bg={marqueeBg}
                >
                  <Marquee.Viewport>
                    <Marquee.Content>
                      {networks.map((n) => (
                        <Marquee.Item key={n.id}>
                          <Link
                            to={generatePath(NetworksRoutes.MyShow, {
                              slug: n.slug,
                            })}
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: 'relative', zIndex: 1 }}
                          >
                            <HStack gap={2} px={5} py={3}>
                              <NetworkLogoBox
                                network={n}
                                logoBg={networkLogoBg}
                              />
                              <Text
                                fontSize="12px"
                                fontWeight={600}
                                color={networkNameColor}
                                whiteSpace="nowrap"
                                _hover={{ textDecoration: 'underline' }}
                              >
                                {n.name}
                              </Text>
                            </HStack>
                          </Link>
                        </Marquee.Item>
                      ))}
                    </Marquee.Content>
                  </Marquee.Viewport>
                  <Marquee.Edge side="end" />
                </Marquee.Root>
              )}
            </HStack>
          ) : (
            <HStack
              px={4}
              py={3}
              gap={2}
              bg={noNetworkBg}
              borderTop="1px solid"
              borderColor={noNetworkBorder}
              flexShrink={0}
            >
              <Box color={noNetworkColor} flexShrink={0}>
                <LuHeartCrack size={13} />
              </Box>
              <Text fontSize="12px" color={noNetworkColor}>
                {t('communities.card.networks.none')}
              </Text>
            </HStack>
          ))}

        {footer && (
          <Box px={5} pb={4} position="relative" zIndex={2}>
            {footer}
          </Box>
        )}
      </Card.Root>
    )

    return (
      <>
        <Box position="relative" h={isFullHeight ? 'full' : undefined}>
          {cardInner}
          {canLeave && (
            <Box position="absolute" top={3} right={3} zIndex={2}>
              <ThreeDotsMenu
                ariaLabel={t(
                  'communities.my_communities.index.table.actions.label'
                )}
                buttons={[
                  {
                    key: 'leave',
                    label: t(
                      'communities.my_communities.index.table.actions.leave'
                    ),
                    icon: <LuPowerOff />,
                    destructive: true,
                  },
                ]}
                onSelect={(key) => {
                  if (key === 'leave') setLeaveOpen(true)
                }}
              />
            </Box>
          )}
        </Box>

        {canLeave && circleMembership && (
          <DialogRoot
            lazyMount
            open={leaveOpen}
            onOpenChange={(e: { open: boolean }) => setLeaveOpen(e.open)}
            placement="center"
            closeOnEscape
          >
            <DialogContent bg={leaveDialogBg} zIndex={1600}>
              <DialogHeader>
                <Heading size="lg" fontWeight="bold">
                  {t(
                    'communities.my_communities.index.table.actions.leave_confirm_title'
                  )}
                </Heading>
              </DialogHeader>
              <DialogCloseTrigger />
              <DialogBody>
                {t(
                  'communities.my_communities.index.table.actions.leave_confirm_description',
                  { name: circle.name }
                )}
              </DialogBody>
              <DialogFooter>
                <HStack gap={3}>
                  <DialogActionTrigger asChild>
                    <Button variant="outline">{t('global:cancel')}</Button>
                  </DialogActionTrigger>
                  <Button
                    colorPalette="red"
                    loading={leaveLoading}
                    onClick={async () => {
                      try {
                        await leaveCircleMembership({
                          variables: { id: circleMembership.id },
                        })
                        toast.success(
                          t('communities.show.actions.quit.success', {
                            name: circle.name,
                          })
                        )
                        setLeaveOpen(false)
                      } catch {
                        toast.error(t('communities.show.actions.quit.error'))
                      }
                    }}
                  >
                    {t('communities.my_communities.index.table.actions.leave')}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogRoot>
        )}
      </>
    )
  }
)
